import { useEffect, useState } from "react";
import { Sidebar } from "../../components/sidebar";
import { Snackbar } from "../../components/snackbar";
import { deleteUser, findAllUsers, updateUser, findUserById } from "../../services/user";
import { useNavigate } from "react-router-dom";
import PeopleIcon from "@mui/icons-material/People";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import EmailIcon from "@mui/icons-material/Email";
import BadgeIcon from "@mui/icons-material/Badge";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import BuildIcon from "@mui/icons-material/Build";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import WarningIcon from "@mui/icons-material/Warning";
import LockIcon from "@mui/icons-material/Lock";
import { Wrapper } from "../../components/wrapper";
import { DEFAULT_SNACK } from "../../utils/snacks";

const RoleBadge = ({ role }) => {
  const roleConfig = {
    admin: {
      text: "Administrador",
      className: "bg-purple-500/10 text-purple-400 border-purple-500/20",
      icon: AdminPanelSettingsIcon,
    },
    mechanic: {
      text: "Mecánico",
      className: "bg-blue-500/10 text-blue-400 border-blue-500/20",
      icon: BuildIcon,
    },
    user: {
      text: "Usuario",
      className: "bg-green-500/10 text-green-400 border-green-500/20",
      icon: BadgeIcon,
    },
  };

  const config = roleConfig[role?.toLowerCase()] || roleConfig.user;
  const Icon = config.icon;

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-full border ${config.className}`}
    >
      <Icon sx={{ fontSize: 14 }} />
      {config.text}
    </span>
  );
};

export const ListUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "",
    last_name: "",
    email: "",
    role: "",
    password: "",
  });
  const [snack, setSnackbar] = useState(DEFAULT_SNACK);
  const navigate = useNavigate();

  const roles = ["admin", "user", "mechanic"];

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const data = await findAllUsers();
        setUsers(data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching users:", error);
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  const handleDelete = async () => {
    try {
      await deleteUser(selectedUser._id);
      setUsers(users.filter((user) => user._id !== selectedUser._id));
      setShowDeleteModal(false);
      setSelectedUser(null);
      setSnackbar({
        visible: true,
        severity: "success",
        message: "Usuario eliminado exitosamente",
      });
      setTimeout(() => setSnackbar(DEFAULT_SNACK), 3000);
    } catch (error) {
      console.error("Error deleting user:", error);
      setSnackbar({
        visible: true,
        severity: "error",
        message: "Error al eliminar usuario",
      });
      setTimeout(() => setSnackbar(DEFAULT_SNACK), 3000);
    }
  };

  const handleEdit = async (user) => {
    setSelectedUser(user);
    setEditForm({
      name: user.name,
      last_name: user.last_name,
      email: user.email,
      role: user.role,
      password: "",
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    try {
      const payload = {
        name: editForm.name,
        last_name: editForm.last_name,
        email: editForm.email,
        role: editForm.role,
      };
      
      if (editForm.password) {
        payload.password = editForm.password;
      }

      await updateUser(payload, selectedUser._id);
      
      const updatedUsers = users.map((user) =>
        user._id === selectedUser._id
          ? { ...user, ...payload }
          : user
      );
      setUsers(updatedUsers);
      setShowEditModal(false);
      setSelectedUser(null);
      setSnackbar({
        visible: true,
        severity: "success",
        message: "Usuario actualizado exitosamente",
      });
      setTimeout(() => setSnackbar(DEFAULT_SNACK), 3000);
    } catch (error) {
      console.error("Error updating user:", error);
      setSnackbar({
        visible: true,
        severity: "error",
        message: "Error al actualizar usuario",
      });
      setTimeout(() => setSnackbar(DEFAULT_SNACK), 3000);
    }
  };

  const openDeleteModal = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const handleAddNew = () => {
    navigate("/dashboard/newuser");
  };

  if (loading) {
    return (
      <div className="flex min-h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center p-4 bg-sidebar-bg">
          <Wrapper>
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-300">Cargando usuarios...</p>
            </div>
          </Wrapper>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-sidebar-bg">
      <Sidebar />
      <div className="flex-1 flex flex-col p-4">
        <Wrapper>
          <div className="flex-1 p-6 lg:p-8">
            <div className="max-w-7xl mx-auto">
              <div className="mb-8 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center">
                      <PeopleIcon className="text-white" fontSize="small" />
                    </div>
                    <h1 className="text-3xl font-bold text-white">Usuarios</h1>
                  </div>
                  <p className="text-gray-400 text-sm mt-1">
                    Gestiona todos los usuarios del sistema
                  </p>
                </div>
                <button
                  onClick={handleAddNew}
                  className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200 flex items-center gap-2 shadow-sm hover:shadow-md"
                >
                  <PersonAddIcon fontSize="small" />
                  Nuevo Usuario
                </button>
              </div>

              <div className="bg-sidebar-bg rounded-xl shadow-sm border border-sidebar-accent overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-sidebar-accent bg-sidebar-bg">
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                          <div className="flex items-center gap-2">
                            <BadgeIcon
                              fontSize="small"
                              className="text-gray-400"
                            />
                            Nombre
                          </div>
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                          Apellido
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                          <div className="flex items-center gap-2">
                            <EmailIcon
                              fontSize="small"
                              className="text-gray-400"
                            />
                            Email
                          </div>
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                          <div className="flex items-center gap-2">
                            <AdminPanelSettingsIcon
                              fontSize="small"
                              className="text-gray-400"
                            />
                            Rol
                          </div>
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-sidebar-accent bg-black">
                      {users.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="px-6 py-12 text-center">
                            <PeopleIcon
                              className="mx-auto text-gray-300 mb-3"
                              fontSize="large"
                            />
                            <p className="text-gray-500 text-sm">
                              No hay usuarios registrados
                            </p>
                          </td>
                        </tr>
                      ) : (
                        users.map((user) => (
                          <tr
                            key={user._id}
                            className="hover:bg-sidebar-accent transition-colors duration-150"
                          >
                            <td className="px-6 py-4">
                              <span className="text-sm font-medium text-white uppercase">
                                {user.name}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <span className="text-sm text-gray-200 capitalize">
                                {user.last_name}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <span className="text-sm text-gray-200">
                                {user.email}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <RoleBadge role={user.role} />
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => handleEdit(user)}
                                  className="p-2 text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors duration-150 border border-transparent hover:border-blue-500/30"
                                  title="Editar usuario"
                                >
                                  <EditIcon fontSize="small" />
                                </button>
                                <button
                                  onClick={() => openDeleteModal(user)}
                                  className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors duration-150 border border-transparent hover:border-red-500/30"
                                  title="Eliminar usuario"
                                >
                                  <DeleteIcon fontSize="small" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {users.length > 0 && (
                  <div className="px-6 py-4 border-t border-sidebar-accent bg-sidebar-bg">
                    <p className="text-sm text-gray-300">
                      Total:{" "}
                      <span className="font-semibold text-white">
                        {users.length}
                      </span>{" "}
                      {users.length === 1 ? "usuario" : "usuarios"}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Wrapper>
      </div>

      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-sidebar-bg rounded-xl border border-sidebar-accent w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-sidebar-accent flex items-center justify-between bg-sidebar-accent/30">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/10 rounded-lg">
                  <EditIcon className="text-blue-400" sx={{ fontSize: 20 }} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Editar Usuario</h3>
                  <p className="text-sm text-gray-400">Modifica los datos del usuario</p>
                </div>
              </div>
              <button
                onClick={() => setShowEditModal(false)}
                className="p-2 hover:bg-sidebar-accent rounded-lg transition-colors"
              >
                <CloseIcon className="text-gray-400" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Nombre *
                  </label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    className="w-full px-4 py-2 bg-white rounded-lg text-black border-2 border-sidebar-accent focus:border-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Apellido *
                  </label>
                  <input
                    type="text"
                    value={editForm.last_name}
                    onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                    className="w-full px-4 py-2 bg-white rounded-lg text-black border-2 border-sidebar-accent focus:border-blue-500 focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="w-full px-4 py-2 bg-white rounded-lg text-black border-2 border-sidebar-accent focus:border-blue-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Rol *
                </label>
                <select
                  value={editForm.role}
                  onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                  className="w-full px-4 py-2 bg-white rounded-lg text-black border-2 border-sidebar-accent focus:border-blue-500 focus:outline-none"
                >
                  {roles.map((role) => (
                    <option key={role} value={role}>
                      {role.charAt(0).toUpperCase() + role.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-400 mb-2 flex items-center gap-2">
                  <LockIcon sx={{ fontSize: 16 }} />
                  Nueva Contraseña (opcional)
                </label>
                <input
                  type="password"
                  value={editForm.password}
                  onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                  placeholder="Dejar vacío para no cambiar"
                  className="w-full px-4 py-2 bg-white rounded-lg text-black border-2 border-sidebar-accent focus:border-blue-500 focus:outline-none"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Solo completa este campo si deseas cambiar la contraseña
                </p>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-sidebar-accent flex justify-end gap-3">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveEdit}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2"
              >
                <SaveIcon sx={{ fontSize: 18 }} />
                Guardar Cambios
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-sidebar-bg rounded-xl border border-red-500/30 w-full max-w-md">
            <div className="px-6 py-4 border-b border-red-500/30 bg-red-500/10">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-500/20 rounded-lg">
                  <WarningIcon className="text-red-400" sx={{ fontSize: 24 }} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Confirmar Eliminación</h3>
                  <p className="text-sm text-gray-400">Esta acción no se puede deshacer</p>
                </div>
              </div>
            </div>

            <div className="p-6">
              <p className="text-gray-300 mb-4">
                ¿Estás seguro de que deseas eliminar al usuario{" "}
                <span className="font-semibold text-white">
                  {selectedUser?.name} {selectedUser?.last_name}
                </span>
                ?
              </p>
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                <p className="text-sm text-red-400 flex items-start gap-2">
                  <WarningIcon sx={{ fontSize: 16, marginTop: "2px" }} />
                  <span>
                    Se eliminará permanentemente el usuario y no podrá acceder al sistema.
                  </span>
                </p>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-sidebar-accent flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedUser(null);
                }}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center gap-2"
              >
                <DeleteIcon sx={{ fontSize: 18 }} />
                Eliminar Usuario
              </button>
            </div>
          </div>
        </div>
      )}

      <Snackbar
        severity={snack.severity}
        message={snack.message}
        visible={snack.visible}
      />
    </div>
  );
};
